import React, { useState, useRef, useMemo } from 'react';
import ConfirmModal from '../components/common/ConfirmModal';
import CSVImportGuide from '../components/contacts/CSVImportGuide';
import ContactsImportCorrection from '../components/contacts/ContactsImportCorrection';
import ContactSearch from '../components/contacts/ContactSearch';
import { Link } from 'react-router-dom';
import MainLayout from '../components/layout/MainLayout';
import {
  useGetContactsQuery,
  useSearchContactsQuery,
  useCreateContactMutation,
  useDeleteContactMutation,
  useGetContactGroupsQuery,
  useCreateContactGroupMutation,
  useUpdateContactGroupMutation,
  useDeleteContactGroupMutation,
  useGetContactGroupQuery,
  useAddGroupMembersMutation,
  useRemoveGroupMembersMutation,
  useImportContactsCSVMutation,
  Contact,
  ContactGroup
} from '../store/services/whatsappApi';

const Contacts: React.FC = () => {
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50); // Show 50 contacts per page

  const { data: contactsData, isLoading, refetch } = useGetContactsQuery({
    limit: pageSize,
    skip: (currentPage - 1) * pageSize
  });

  // Extract contacts and pagination info
  const contacts = useMemo(() => contactsData?.contacts || [], [contactsData?.contacts]);
  const totalContacts = contactsData?.total || 0;
  const totalPages = Math.ceil(totalContacts / pageSize);

  const [searchResults, setSearchResults] = useState<Contact[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [createContact, { isLoading: isCreating }] = useCreateContactMutation();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [deleteContact, { isLoading: isDeleting }] = useDeleteContactMutation();
  
  // Import contacts CSV mutation
  const [importContactsCSV, { isLoading: isImporting }] = useImportContactsCSVMutation();
  const isBulkDeleting = false;
  
  // State for CSV import guide modal
  const [showImportGuide, setShowImportGuide] = useState(false);
  
  // Refs for file inputs
  const contactsFileInputRef = useRef<HTMLInputElement>(null);
  const groupImportFileInputRef = useRef<HTMLInputElement>(null);
  
  // State for new contact form
  const [showForm, setShowForm] = useState(false);
  const [newContact, setNewContact] = useState({
    name: '',
    phone_number: '',
    email: '',
    notes: ''
  });
  const [formError, setFormError] = useState('');
  const [importError, setImportError] = useState('');
  const [importSuccess, setImportSuccess] = useState('');
  const [invalidContacts, setInvalidContacts] = useState<any[]>([]);
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState<number[]>([]);
  const [selectMode, setSelectMode] = useState(false);
  // Confirmation modals state
  const [isDeleteContactOpen, setIsDeleteContactOpen] = useState(false);
  const [contactIdToDelete, setContactIdToDelete] = useState<number | null>(null);
  const [isBulkDeleteOpen, setIsBulkDeleteOpen] = useState(false);
  const [isDeleteGroupOpen, setIsDeleteGroupOpen] = useState(false);
  const [groupIdToDelete, setGroupIdToDelete] = useState<number | null>(null);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewContact(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    
    // Basic validation
    if (!newContact.name || !newContact.phone_number) {
      setFormError('Name and phone number are required');
      return;
    }
    
    // Validate phone number format
    const phoneRegex = /^\+[0-9]{1,3}[0-9]{9,14}$/;
    if (!phoneRegex.test(newContact.phone_number)) {
      setFormError('Phone number must be in international format (e.g., +254712345678)');
      return;
    }
    
    try {
      // Build payload to avoid sending empty email or unsupported fields
      const payload: any = {
        name: newContact.name,
        phone_number: newContact.phone_number,
      };
      if (newContact.email && newContact.email.trim() !== '') {
        payload.email = newContact.email.trim();
      }

      // Use unwrap so errors from the API throw and are caught below
      await createContact(payload).unwrap();
      // Reset form and hide it
      setNewContact({ name: '', phone_number: '', email: '', notes: '' });
      setShowForm(false);
      // Refetch contacts to update the list
      refetch();
    } catch (error) {
      console.error('Failed to create contact:', error);
      setFormError('Failed to create contact. Please try again.');
    }
  };
  
  // Handle general contacts import
  const handleImportClick = () => {
    setShowImportGuide(true);
  };
  
  // Handle actual file selection after viewing guide
  const handleStartImport = () => {
    if (contactsFileInputRef.current) {
      contactsFileInputRef.current.click();
    }
    setShowImportGuide(false);
  };
  
  // Handle group-specific import
  const handleGroupImportClick = () => {
    if (!activeGroupId) {
      alert('Please select a group first');
      return;
    }
    
    if (groupImportFileInputRef.current) {
      groupImportFileInputRef.current.click();
    }
  };
  
  // Handle general contacts import file selection
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check if the file is CSV
    if (!file.name.endsWith('.csv')) {
      setImportError('Please select a valid CSV file');
      setImportSuccess('');
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const result = await importContactsCSV(formData).unwrap();
      
      if (result.success) {
        // Check if there are invalid contacts that need correction
        if (result.invalid_contacts && result.invalid_contacts.length > 0) {
          setInvalidContacts(result.invalid_contacts);
          setShowCorrectionModal(true);
          setImportSuccess(`Found ${result.count} valid contacts and ${result.invalid_contacts.length} contacts that need correction`);
        } else {
          let successMessage = `Successfully imported ${result.count} contacts`;
          if (result.skipped > 0) {
            successMessage += `, ${result.skipped} skipped`;
          }
          setImportSuccess(successMessage);
        }
        
        if (result.errors && result.errors.length > 0) {
          setImportError(`There were some errors during import: ${result.errors[0]}${result.errors.length > 1 ? ` and ${result.errors.length - 1} more` : ''}`);
        } else {
          setImportError('');
        }
      } else {
        setImportError('Failed to import contacts. Please check the CSV format.');
        setImportSuccess('');
      }
      
      refetch(); // Refresh the contact list
      
      // Reset the file input
      if (contactsFileInputRef.current) {
        contactsFileInputRef.current.value = '';
      }
    } catch (error: any) {
      console.error('Failed to import contacts:', error);
      setImportError(error.data?.detail || 'Failed to import contacts. Please check the CSV format.');
      setImportSuccess('');
    }
  };
  
  // Handle group-specific import file selection
  const handleGroupImportFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!activeGroupId) {
      alert('Please select a group first');
      return;
    }
    
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check if the file is CSV
    if (!file.name.endsWith('.csv')) {
      setImportError('Please select a valid CSV file');
      setImportSuccess('');
      return;
    }
    
    try {
      // First import the contacts
      const formData = new FormData();
      formData.append('file', file);
      formData.append('group_id', activeGroupId.toString()); // Pass the group ID
      
      const result = await importContactsCSV(formData).unwrap();
      
      if (result.success) {
        // Check if there are invalid contacts that need correction
        if (result.invalid_contacts && result.invalid_contacts.length > 0) {
          setInvalidContacts(result.invalid_contacts);
          setShowCorrectionModal(true);
          setImportSuccess(`Found ${result.count} valid contacts and ${result.invalid_contacts.length} contacts that need correction`);
        } else {
          let successMessage = `Successfully imported ${result.count} contacts to the group`;
          if (result.skipped > 0) {
            successMessage += `, ${result.skipped} skipped`;
          }
          setImportSuccess(successMessage);
        }
        
        if (result.errors && result.errors.length > 0) {
          setImportError(`There were some errors during import: ${result.errors[0]}${result.errors.length > 1 ? ` and ${result.errors.length - 1} more` : ''}`);
        } else {
          setImportError('');
        }
      } else {
        setImportError('Failed to import contacts to group. Please check the CSV format.');
        setImportSuccess('');
      }
      
      // Refresh data
      refetch();
      
      // Reset the file input
      if (groupImportFileInputRef.current) {
        groupImportFileInputRef.current.value = '';
      }
    } catch (error: any) {
      console.error('Failed to import contacts to group:', error);
      setImportError(error.data?.detail || 'Failed to import contacts to group. Please check the CSV format.');
      setImportSuccess('');
    }
  };
  
  // Handle contact export - this will be implemented later
  const handleExport = async () => {
    alert('Export functionality will be implemented in a future update');
  };
  
  // Handle contact deletion
  const handleDeleteContact = (id: number) => {
    setContactIdToDelete(id);
    setIsDeleteContactOpen(true);
  };
  const confirmDeleteContact = async () => {
    if (contactIdToDelete == null) return;
    try {
      await deleteContact(contactIdToDelete);
      setIsDeleteContactOpen(false);
      setContactIdToDelete(null);
      refetch();
    } catch (error) {
      console.error('Failed to delete contact:', error);
    }
  };
  
  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedContacts.length === 0) {
      alert('Please select at least one contact to delete');
      return;
    }
    setIsBulkDeleteOpen(true);
  };
  const confirmBulkDelete = async () => {
    try {
      for (const contactId of selectedContacts) {
        await deleteContact(contactId);
      }
      setSelectedContacts([]);
      setSelectMode(false);
      setIsBulkDeleteOpen(false);
      refetch();
    } catch (error) {
      alert('Failed to delete contacts');
    }
  };
  
  const toggleSelectMode = () => {
    setSelectMode(!selectMode);
    if (selectMode) {
      setSelectedContacts([]);
    }
  };
  
  // Handle contact selection
  const toggleContactSelection = (id: number) => {
    if (selectedContacts.includes(id)) {
      setSelectedContacts(selectedContacts.filter(contactId => contactId !== id));
    } else {
      setSelectedContacts([...selectedContacts, id]);
    }
  };
  
  // Function to format phone numbers for display
  const formatPhoneNumber = (phone: string) => {
    // Keep the international format but add spaces for readability
    // Example: +254712345678 -> +254 71 234 5678
    if (phone.startsWith('+')) {
      const country = phone.substring(1, 4);
      const rest = phone.substring(4);
      return `+${country} ${rest.substring(0, 2)} ${rest.substring(2, 5)} ${rest.substring(5)}`;
    }
    return phone;
  };

  // State for group management
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [currentGroup, setCurrentGroup] = useState<ContactGroup | null>(null);
  const [newGroup, setNewGroup] = useState({ name: '', description: '' });
  const [groupFormError, setGroupFormError] = useState('');
  const { data: groups, isLoading: isLoadingGroups } = useGetContactGroupsQuery();
  const [createGroup, { isLoading: isCreatingGroup }] = useCreateContactGroupMutation();
  const [updateGroup, { isLoading: isUpdatingGroup }] = useUpdateContactGroupMutation();
  const [deleteGroup] = useDeleteContactGroupMutation();
  const [addGroupMembers, { isLoading: isAddingMembers }] = useAddGroupMembersMutation();
  const [removeGroupMembers, { isLoading: isRemovingMembers }] = useRemoveGroupMembersMutation();
  
  // State for active group
  const [activeGroupId, setActiveGroupId] = useState<number | null>(null);

  // Reset to page 1 when search becomes active
  React.useEffect(() => {
    if (isSearchActive) {
      setCurrentPage(1);
    }
  }, [isSearchActive]);

  // Reset to page 1 when switching groups
  React.useEffect(() => {
    setCurrentPage(1);
  }, [activeGroupId]);

  // State for add contacts to group modal
  const [showAddToGroupModal, setShowAddToGroupModal] = useState(false);
  const [selectedGroupForAdd, setSelectedGroupForAdd] = useState<ContactGroup | null>(null);
  const [modalSearchTerm, setModalSearchTerm] = useState('');
  
  // Active group data with full member contacts
  const { data: activeGroupData, isLoading: isLoadingGroup } = useGetContactGroupQuery(activeGroupId || 0, {
    skip: !activeGroupId,
  });

  // Search contacts for the add to group modal
  const { data: modalSearchResults, isLoading: isModalSearching } = useSearchContactsQuery(
    { query: modalSearchTerm, limit: 500 },
    { skip: !modalSearchTerm.trim() }
  );
  
  // Filter contacts based on active group and search results
  const displayedContacts = useMemo(() => {
    // If search is active, use search results
    if (isSearchActive && searchResults.length > 0) {
      if (activeGroupId) {
        // Filter search results by active group
        // If group data is still loading, show all search results
        if (isLoadingGroup || !activeGroupData) {
          return searchResults;
        }
        // Filter search results to only include contacts that are in the active group
        const groupMemberIds = activeGroupData.members.map(member => member.id);
        return searchResults.filter(contact => groupMemberIds.includes(contact.id));
      }
      return searchResults;
    }

    // Otherwise use regular contacts filtered by group
    if (!contacts) return [];

    if (activeGroupId) {
      // If group data is still loading, show all contacts temporarily
      if (isLoadingGroup || !activeGroupData) {
        return contacts;
      }
      // Return the member contacts directly from the group data
      return activeGroupData.members;
    }

    return contacts;
  }, [contacts, activeGroupId, searchResults, isSearchActive, isLoadingGroup, activeGroupData]);

  // Group form handlers
  const handleGroupInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewGroup(prev => ({ ...prev, [name]: value }));
  };
  
  const handleGroupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setGroupFormError('');
    
    if (!newGroup.name) {
      setGroupFormError('Group name is required');
      return;
    }
    
    try {
      if (currentGroup) {
        await updateGroup({ id: currentGroup.id, body: newGroup }).unwrap();
      } else {
        const result = await createGroup(newGroup).unwrap();
        setActiveGroupId(result.id); // Set newly created group as active
      }
      
      // Reset form and hide it
      setNewGroup({ name: '', description: '' });
      setCurrentGroup(null);
      setShowGroupForm(false);
    } catch (error) {
      console.error('Failed to save contact group:', error);
      setGroupFormError('Failed to save contact group. Please try again.');
    }
  };
  
  const handleEditGroup = (group: ContactGroup) => {
    setCurrentGroup(group);
    setNewGroup({ name: group.name, description: group.description || '' });
    setShowGroupForm(true);
  };
  
  const handleDeleteGroup = (groupId: number) => {
    setGroupIdToDelete(groupId);
    setIsDeleteGroupOpen(true);
  };
  const confirmDeleteGroup = async () => {
    if (groupIdToDelete == null) return;
    try {
      await deleteGroup(groupIdToDelete);
      if (activeGroupId === groupIdToDelete) {
        setActiveGroupId(null);
      }
      setIsDeleteGroupOpen(false);
      setGroupIdToDelete(null);
    } catch (error) {
      console.error('Failed to delete group:', error);
    }
  };
  
  const handleAddSelectedContactsToGroup = async () => {
    if (!activeGroupId || selectedContacts.length === 0) return;

    try {
      await addGroupMembers({
        groupId: activeGroupId,
        contactIds: selectedContacts
      });

      // Clear selection after adding
      setSelectedContacts([]);
      setSelectMode(false);
    } catch (error) {
      console.error('Failed to add contacts to group:', error);
    }
  };

  const handleRemoveContactFromGroup = async (contactId: number) => {
    if (!activeGroupId) return;

    try {
      await removeGroupMembers({
        groupId: activeGroupId,
        contactIds: [contactId]
      });
    } catch (error) {
      console.error('Failed to remove contact from group:', error);
    }
  };
  
  return (
    <MainLayout>
      <div className="container mx-auto px-4 py-6">
      {/* Hidden file inputs for importing contacts */}
      <input 
        type="file" 
        ref={contactsFileInputRef} 
        onChange={handleFileSelect} 
        style={{ display: 'none' }} 
        accept=".csv"
      />
      
      {/* Hidden file input for importing contacts directly to a group */}
      <input 
        type="file" 
        ref={groupImportFileInputRef} 
        onChange={handleGroupImportFileSelect} 
        style={{ display: 'none' }} 
        accept=".csv"
      />
      
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <h1 className="text-2xl font-bold text-gray-800 mr-4">Contacts</h1>
          <button
            onClick={toggleSelectMode}
            className={`px-3 py-1 rounded-md mr-2 text-sm ${selectMode 
              ? 'bg-gray-700 text-white' 
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
          >
            {selectMode ? 'Cancel Selection' : 'Select Contacts'}
          </button>
        </div>
        
        <div className="flex space-x-2">
          <button
            onClick={handleImportClick}
            disabled={isImporting}
            className="bg-blue-500 text-white px-3 py-1 rounded-md hover:bg-blue-600 text-sm flex items-center"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
            </svg>
            {isImporting ? 'Importing...' : 'Import CSV'}
          </button>
          
          <button
            onClick={handleExport}
            className="bg-green-600 text-white px-3 py-1 rounded-md hover:bg-green-700 text-sm flex items-center"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
            </svg>
            Export CSV
          </button>
          
          <button
            onClick={() => setShowForm(!showForm)}
            className="bg-whatsapp-green text-white px-3 py-1 rounded-md hover:bg-whatsapp-dark-green text-sm"
          >
            {showForm ? 'Cancel' : 'Add Contact'}
          </button>
        </div>
      </div>

      {/* Search bar */}
      <div className="mb-4">
        <ContactSearch
          onSearchResults={(results) => {
            setSearchResults(results);
          }}
          onSearching={setIsSearching}
          onSearchActive={setIsSearchActive}
        />
      </div>

      {/* Import success/error messages */}
      {importSuccess && (
        <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">{importSuccess}</p>
            </div>
          </div>
        </div>
      )}
      
      {importError && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{importError}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Bulk actions when in select mode */}
      {selectMode && selectedContacts.length > 0 && (
        <div className="bg-gray-100 p-3 rounded-md mb-4 flex justify-between items-center">
          <p className="text-sm text-gray-700">{selectedContacts.length} contacts selected</p>
          <div className="flex space-x-2">
            {activeGroupId && (
              <button
                onClick={handleAddSelectedContactsToGroup}
                className="bg-blue-500 text-white px-3 py-1 rounded-md hover:bg-blue-600 text-sm flex items-center"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add to Group
              </button>
            )}
            <button
              onClick={handleBulkDelete}
              disabled={isBulkDeleting}
              className="bg-red-500 text-white px-3 py-1 rounded-md hover:bg-red-600 text-sm flex items-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              {isBulkDeleting ? 'Deleting...' : 'Delete Selected'}
            </button>
          </div>
        </div>
      )}
      
      {/* New Contact Form */}
      {showForm && (
        <div className="bg-white shadow-md rounded-md p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Add New Contact</h2>
          
          {formError && (
            <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{formError}</p>
                </div>
              </div>
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  name="name"
                  value={newContact.name}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-whatsapp-green"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                <input
                  type="text"
                  name="phone_number"
                  value={newContact.phone_number}
                  onChange={handleInputChange}
                  placeholder="+254712345678"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-whatsapp-green"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  name="email"
                  value={newContact.email}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-whatsapp-green"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  name="notes"
                  value={newContact.notes}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-whatsapp-green"
                ></textarea>
              </div>
            </div>
            
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="mr-2 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isCreating}
                className="px-4 py-2 bg-whatsapp-green text-white rounded-md hover:bg-whatsapp-dark-green focus:outline-none focus:ring-2 focus:ring-whatsapp-green"
              >
                {isCreating ? 'Saving...' : 'Save Contact'}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Group Management */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Contact Groups</h2>
          <button
            onClick={() => {
              setCurrentGroup(null);
              setNewGroup({ name: '', description: '' });
              setShowGroupForm(true);
            }}
            className="bg-whatsapp-green text-white px-3 py-1 rounded-md hover:bg-whatsapp-dark-green text-sm"
          >
            Add Group
          </button>
        </div>

        {/* Group Form */}
        {showGroupForm && (
          <div className="bg-white shadow-md rounded-md p-6 mb-4">
            <h3 className="text-lg font-semibold mb-4">{currentGroup ? 'Edit Group' : 'Add New Group'}</h3>
            
            {groupFormError && (
              <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700">{groupFormError}</p>
                  </div>
                </div>
              </div>
            )}
            
            <form onSubmit={handleGroupSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Group Name *</label>
                <input
                  type="text"
                  name="name"
                  value={newGroup.name}
                  onChange={handleGroupInputChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-whatsapp-green"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  name="description"
                  value={newGroup.description}
                  onChange={handleGroupInputChange}
                  rows={3}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-whatsapp-green"
                ></textarea>
              </div>
              
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowGroupForm(false)}
                  className="mr-2 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCreatingGroup || isUpdatingGroup}
                  className="px-4 py-2 bg-whatsapp-green text-white rounded-md hover:bg-whatsapp-dark-green focus:outline-none"
                >
                  {currentGroup ? 'Update Group' : 'Create Group'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Group List */}
        <div className="bg-white shadow-md rounded-md mb-6 overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-12">
            {/* Group Sidebar */}
            <div className="md:col-span-3 border-r border-gray-200 max-h-96 overflow-y-auto">
              <div 
                className={`py-3 px-4 cursor-pointer hover:bg-gray-50 border-l-4 ${!activeGroupId ? 'bg-blue-50 border-blue-500' : 'border-transparent'}`}
                onClick={() => setActiveGroupId(null)}
              >
                <div className="font-semibold">All Contacts</div>
                <div className="text-xs text-gray-500">{totalContacts || 0} contacts</div>
              </div>
              
              {isLoadingGroups ? (
                <div className="p-4 text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-whatsapp-green mx-auto"></div>
                </div>
              ) : groups && groups.length > 0 ? (
                groups.map(group => (
                  <div 
                    key={group.id}
                    className={`py-3 px-4 cursor-pointer hover:bg-gray-50 border-l-4 ${activeGroupId === group.id ? 'bg-blue-50 border-blue-500' : 'border-transparent'}`}
                    onClick={() => setActiveGroupId(group.id)}
                  >
                    <div className="flex justify-between items-center">
                      <div className="font-semibold">{group.name}</div>
                      <div className="flex space-x-1">
                        <button onClick={(e) => {
                          e.stopPropagation();
                          setSelectedGroupForAdd(group);
                          setShowAddToGroupModal(true);
                        }} className="text-gray-500 hover:text-blue-600" title="Add existing contacts to this group">
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                        </button>
                        <button onClick={(e) => {
                          e.stopPropagation();
                          setActiveGroupId(group.id);
                          handleGroupImportClick();
                        }} className="text-gray-500 hover:text-green-500" title="Import contacts to this group">
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                          </svg>
                        </button>
                        <button onClick={(e) => {
                          e.stopPropagation();
                          handleEditGroup(group);
                        }} className="text-gray-500 hover:text-blue-500" title="Edit group">
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteGroup(group.id);
                        }} className="text-gray-500 hover:text-red-500" title="Delete group">
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">{group.memberCount || 0} contacts</div>
                  </div>
                ))
              ) : (
                <div className="p-4 text-center text-gray-500">
                  No contact groups yet
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Contacts List */}
      <div className="bg-white shadow-md rounded-md overflow-hidden">
        {isLoading || isSearching ? (
          <div className="flex justify-center items-center p-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-whatsapp-green"></div>
          </div>
        ) : displayedContacts && displayedContacts.length > 0 ? (
          <ul className="divide-y divide-gray-200">
            {displayedContacts.map((contact: Contact) => (
              <li key={contact.id} className={`${selectMode ? 'hover:bg-gray-100' : 'hover:bg-gray-50'}`}>
                <div className="p-4">
                  <div className="flex items-center">
                    {selectMode && (
                      <div className="mr-3">
                        <input 
                          type="checkbox"
                          checked={selectedContacts.includes(contact.id)}
                          onChange={() => toggleContactSelection(contact.id)}
                          className="h-4 w-4 text-whatsapp-green focus:ring-whatsapp-green border-gray-300 rounded"
                        />
                      </div>
                    )}
                    <div className="h-10 w-10 rounded-full bg-whatsapp-light-green text-whatsapp-dark-green flex items-center justify-center font-semibold">
                      {contact.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="ml-4 flex-1">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-medium text-gray-900">{contact.name}</h3>
                        <span className="text-sm text-gray-500">
                          {new Date(contact.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="mt-1 flex flex-col sm:flex-row sm:items-center">
                        <p className="text-sm text-gray-700">{formatPhoneNumber(contact.phone_number)}</p>
                        {contact.email && (
                          <p className="text-sm text-gray-500 sm:ml-4">
                            {contact.email}
                          </p>
                        )}
                        <div className="flex flex-wrap gap-2 sm:ml-4 mt-1 sm:mt-0">
                          {/* Opt-in status badge */}
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${contact.opt_in_status ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                            <svg className={`-ml-0.5 mr-1.5 h-2 w-2 ${contact.opt_in_status ? 'text-green-400' : 'text-gray-400'}`} fill="currentColor" viewBox="0 0 8 8">
                              <circle cx="4" cy="4" r="3" />
                            </svg>
                            {contact.opt_in_status ? 'Opted In' : 'Not Opted In'}
                          </span>
                          
                          {/* Marketing opt-out badge */}
                          {contact.marketing_opt_out && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              <svg className="-ml-0.5 mr-1.5 h-2 w-2 text-red-400" fill="currentColor" viewBox="0 0 8 8">
                                <circle cx="4" cy="4" r="3" />
                              </svg>
                              Marketing Opted Out
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                     {!selectMode ? (
                       <div className="ml-2 flex items-center space-x-2">
                         <Link
                           to={`/messages/${contact.id}`}
                           className="text-blue-500 hover:text-blue-700 p-1"
                           title="Send Message"
                         >
                           <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                           </svg>
                         </Link>
                         {activeGroupId && (
                           <button
                             onClick={() => handleRemoveContactFromGroup(contact.id)}
                             disabled={isRemovingMembers}
                             className="text-orange-500 hover:text-orange-700 p-1 disabled:opacity-50"
                             title="Remove from Group"
                           >
                             <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                             </svg>
                           </button>
                         )}
                         <button
                           onClick={() => handleDeleteContact(contact.id)}
                           className="text-red-500 hover:text-red-700 p-1"
                           title="Delete Contact"
                         >
                           <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                           </svg>
                         </button>
                       </div>
                    ) : (
                      <div className="ml-2 flex-shrink-0">
                        <svg className="h-5 w-5 text-gray-300" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="p-8 text-center">
            {isSearchActive ? 'No contacts found matching your search.' : 'No contacts found.'}
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-whatsapp-green hover:bg-whatsapp-dark-green focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-whatsapp-green"
            >
              <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
              Add your first contact
            </button>
          </div>
        )}

        {/* Pagination Controls - only show when not searching and not in a group */}
        {!isSearchActive && !activeGroupId && totalContacts > pageSize && (
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-700">Show:</label>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="text-sm border border-gray-300 rounded px-2 py-1"
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
              <span className="text-sm text-gray-700">
                Showing {contacts.length > 0 ? ((currentPage - 1) * pageSize) + 1 : 0} to {Math.min(currentPage * pageSize, totalContacts)} of {totalContacts} contacts
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>

              {/* Page numbers */}
              <div className="flex space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                  if (pageNum > totalPages) return null;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-1 text-sm border rounded-md ${
                        pageNum === currentPage
                          ? 'bg-whatsapp-green text-white border-whatsapp-green'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
      </div>

      {/* Add to Group Modal */}
      {showAddToGroupModal && selectedGroupForAdd && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-screen overflow-hidden">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-semibold">Add Contacts to {selectedGroupForAdd.name}</h3>
               <button
                 onClick={() => {
                   setShowAddToGroupModal(false);
                   setModalSearchTerm('');
                 }}
                 className="text-gray-500 hover:text-gray-700"
               >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="px-6 py-4">
              <div className="mb-4">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search contacts..."
                    value={modalSearchTerm}
                    onChange={(e) => setModalSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-green"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="max-h-80 overflow-y-auto">
                {modalSearchTerm.trim() ? (
                  // Show search results when searching
                  isModalSearching ? (
                    <div className="flex justify-center items-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-whatsapp-green"></div>
                    </div>
                  ) : modalSearchResults?.contacts && modalSearchResults.contacts.length > 0 ? (
                    <div className="divide-y divide-gray-200">
                      {modalSearchResults.contacts.map((contact) => {
                        // Check if this contact is already in the group
                        const isAlreadyInGroup = activeGroupData?.members?.some(member => member.id === contact.id) || false;

                        return (
                          <div
                            key={contact.id}
                            className={`flex items-center p-3 ${isAlreadyInGroup ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
                          >
                            <div className="flex-grow">
                              <div className="font-medium">{contact.name}</div>
                              <div className="text-sm text-gray-500">{formatPhoneNumber(contact.phone_number)}</div>
                            </div>
                            <div>
                              {isAlreadyInGroup ? (
                                <span className="text-green-600 text-sm">Already in group</span>
                              ) : (
                                 <button
                                   onClick={() => {
                                     if (selectedGroupForAdd) {
                                       addGroupMembers({
                                         groupId: selectedGroupForAdd.id,
                                         contactIds: [contact.id]
                                       }).then(() => {
                                         // Refresh group data to update the members list
                                         if (activeGroupId === selectedGroupForAdd.id) {
                                           refetch();
                                         }
                                       }).catch((err: Error) => {
                                         console.error('Failed to add contact to group:', err);
                                         alert('Failed to add contact to group. Please try again.');
                                       });
                                     }
                                   }}
                                   disabled={isAddingMembers}
                                   className="px-3 py-1 bg-whatsapp-green text-white rounded-md text-sm hover:bg-whatsapp-dark-green disabled:opacity-50 disabled:cursor-not-allowed"
                                 >
                                   {isAddingMembers ? 'Adding...' : 'Add'}
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-gray-500">
                      No contacts match your search
                    </div>
                  )
                ) : (
                  // Show all contacts when not searching (limited to avoid performance issues)
                  contacts ? (
                    contacts.length > 0 ? (
                      <div className="divide-y divide-gray-200">
                        {contacts.slice(0, 50).map((contact) => {
                          // Check if this contact is already in the group
                          const isAlreadyInGroup = activeGroupData?.members?.some(member => member.id === contact.id) || false;

                          return (
                            <div
                              key={contact.id}
                              className={`flex items-center p-3 ${isAlreadyInGroup ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
                            >
                              <div className="flex-grow">
                                <div className="font-medium">{contact.name}</div>
                                <div className="text-sm text-gray-500">{formatPhoneNumber(contact.phone_number)}</div>
                              </div>
                              <div>
                                {isAlreadyInGroup ? (
                                  <span className="text-green-600 text-sm">Already in group</span>
                                ) : (
                                   <button
                                     onClick={() => {
                                       if (selectedGroupForAdd) {
                                         addGroupMembers({
                                           groupId: selectedGroupForAdd.id,
                                           contactIds: [contact.id]
                                         }).then(() => {
                                           // Refresh group data to update the members list
                                           if (activeGroupId === selectedGroupForAdd.id) {
                                             refetch();
                                           }
                                         }).catch((err: Error) => {
                                           console.error('Failed to add contact to group:', err);
                                           alert('Failed to add contact to group. Please try again.');
                                         });
                                       }
                                     }}
                                     disabled={isAddingMembers}
                                     className="px-3 py-1 bg-whatsapp-green text-white rounded-md text-sm hover:bg-whatsapp-dark-green disabled:opacity-50 disabled:cursor-not-allowed"
                                   >
                                     {isAddingMembers ? 'Adding...' : 'Add'}
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                        {contacts.length > 50 && (
                          <div className="text-center py-2 text-gray-500 text-sm">
                            Showing first 50 contacts. Use search to find specific contacts.
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-4 text-gray-500">
                        No contacts available
                      </div>
                    )
                  ) : (
                    <div className="flex justify-center items-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-whatsapp-green"></div>
                    </div>
                  )
                )}
              </div>
            </div>

              <div className="px-6 py-3 bg-gray-50 flex justify-end">
               <button
                 onClick={() => {
                   setShowAddToGroupModal(false);
                   setModalSearchTerm('');
                 }}
                 className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
               >
                 Close
               </button>
             </div>
          </div>
        </div>
      )}
      {/* Confirm delete contact */}
      <ConfirmModal
        isOpen={isDeleteContactOpen}
        title="Delete Contact"
        message="Are you sure you want to delete this contact? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        destructive
        onConfirm={confirmDeleteContact}
        onCancel={() => { setIsDeleteContactOpen(false); setContactIdToDelete(null); }}
      />

      {/* Confirm bulk delete contacts */}
      <ConfirmModal
        isOpen={isBulkDeleteOpen}
        title="Delete Selected Contacts"
        message={`Are you sure you want to delete ${selectedContacts.length} contacts? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        destructive
        onConfirm={confirmBulkDelete}
        onCancel={() => setIsBulkDeleteOpen(false)}
      />

      {/* Confirm delete group */}
      <ConfirmModal
        isOpen={isDeleteGroupOpen}
        title="Delete Group"
        message="Are you sure you want to delete this group? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        destructive
        onConfirm={confirmDeleteGroup}
        onCancel={() => { setIsDeleteGroupOpen(false); setGroupIdToDelete(null); }}
      />

      {/* CSV Import Guide Modal */}
      {showImportGuide && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <CSVImportGuide 
            onClose={() => setShowImportGuide(false)} 
          />
          <div className="fixed bottom-4 right-4">
            <button
              onClick={handleStartImport}
              className="bg-whatsapp-green text-white px-4 py-2 rounded-md hover:bg-whatsapp-dark-green shadow-lg"
            >
              Start Import
            </button>
          </div>
        </div>
      )}

      {/* Contact Correction Modal */}
      {showCorrectionModal && invalidContacts.length > 0 && (
        <ContactsImportCorrection
          invalidContacts={invalidContacts}
          onClose={() => setShowCorrectionModal(false)}
          onSuccess={() => {
            setShowCorrectionModal(false);
            setInvalidContacts([]);
            refetch();
            setImportSuccess('Successfully imported corrected contacts');
          }}
          groupId={activeGroupId || undefined}
        />
      )}

    </MainLayout>
  );
};

export default Contacts;
